import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PopupBaseComponent } from '../../../../../shared/components/popup/popup-base/popup-base.component';
import { PropertiesService } from '../../../service/properties.service';
import { ManagePmsPopupModel } from './manage-pms-popup.model';
import {
    GuestlinePmsSetup,
    MewsPmsSetup,
    PmsSetup,
    PmsType,
    ShijiPmsSetup,
    StayNTouchPmsSetup,
} from '../../../types/pms';
import { SelectPmsStepComponent } from '../../steps/select-pms-step/select-pms-step.component';
import { ConnectStepComponent } from '../../steps/connect-step/connect-step.component';
import { PmsStatusMsg } from '../../../types/property';
import { LayoutStore, LayoutStoreType } from '../../../../../core/store/layout.store';
import { ApplicationStore, ApplicationStoreType } from '../../../../../core/store/application.store';
import { Toast } from '../../../../../shared/types/toast';
import { uuidv4 } from '../../../../../shared/utils/unique-id-utils';

enum ManagePmsStep {
    SelectPms = 'SELECT_PMS',
    Connect = 'CONNECT',
}

@Component({
    selector: 'juyo-manage-pms-popup',
    styleUrl: './manage-pms-popup.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PopupBaseComponent, SelectPmsStepComponent, ConnectStepComponent],
    template: `
        <juyo-popup-base [popupId]="model().id" [showHeader]="true" [showFooter]="false" [maxWidth]="'700px'">
            <span slot="header">Manage PMS connection</span>

            <div slot="body" class="wizard-body">
                <div class="step-indicator">
                    <div
                        class="step-pill"
                        [class.active]="currentStep() === ManagePmsStep.SelectPms"
                        [class.completed]="currentStep() === ManagePmsStep.Connect"
                    >
                        1. Select PMS
                    </div>
                    <div class="step-pill" [class.active]="currentStep() === ManagePmsStep.Connect">2. Connect</div>
                </div>

                @if (currentStep() === ManagePmsStep.SelectPms) {
                    <juyo-select-pms-step
                        [showBack]="false"
                        [showSkip]="false"
                        (continue)="onSelectPmsContinue($event)"
                        (cancel)="onCancel()"
                        (skip)="onCancel()"
                    />
                }
                @if (currentStep() === ManagePmsStep.Connect) {
                    <juyo-connect-step
                        [pmsType]="selectedPmsType()!"
                        [saving]="isSaving()"
                        [showSkip]="false"
                        [propertyName]="model().property.name"
                        (submit)="onConnectSubmit($event)"
                        (back)="onBack()"
                        (cancel)="onCancel()"
                        (skip)="onCancel()"
                    />
                }
            </div>
        </juyo-popup-base>
    `,
})
export class ManagePmsPopupComponent {
    public model = input.required<ManagePmsPopupModel>();

    protected readonly ManagePmsStep = ManagePmsStep;
    private layoutStore: LayoutStoreType = inject(LayoutStore);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    private propertiesService = inject(PropertiesService);
    private destroyRef = inject(DestroyRef);

    protected currentStep = signal<ManagePmsStep>(ManagePmsStep.SelectPms);
    protected isSaving = signal(false);
    protected selectedPmsType = signal<PmsType | null>(null);

    protected onSelectPmsContinue(pmsType: PmsType): void {
        this.selectedPmsType.set(pmsType);
        this.currentStep.set(ManagePmsStep.Connect);
    }

    protected onConnectSubmit(setup: PmsSetup): void {
        const property = this.model().property;
        const organization = this.applicationStore.selectedOrganization();
        const user = this.applicationStore.signedInUser();

        if (property && organization && user) {
            this.isSaving.set(true);
            if (setup.type === PmsType.Mews) {
                const { apiToken } = setup as MewsPmsSetup;
                this.propertiesService
                    .configureMews(organization.id, property.code, setup.type, apiToken)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        const updatedProperty = property;
                        updatedProperty.pms = PmsType.Mews;
                        updatedProperty.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.model().onUpdated(updatedProperty);
                        this.layoutStore.closePopup(this.model().id);
                    });
            } else if (setup.type === PmsType.Guestline) {
                const { siteId, groupId } = setup as GuestlinePmsSetup;
                this.propertiesService
                    .configureGuestline(organization.id, property.code, setup.type, siteId, groupId)
                    .subscribe(() => {
                        const updatedProperty = property;
                        updatedProperty.pms = PmsType.Guestline;
                        updatedProperty.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.model().onUpdated(updatedProperty);
                        this.layoutStore.closePopup(this.model().id);
                    });
            } else if (setup.type === PmsType.Shiji) {
                const { tenantName, propertyId, password, username } = setup as ShijiPmsSetup;
                this.propertiesService
                    .configureShiji(
                        organization.id,
                        property.code,
                        setup.type,
                        username,
                        tenantName,
                        password,
                        propertyId,
                    )
                    .subscribe(() => {
                        const updatedProperty = property;
                        updatedProperty.pms = PmsType.Shiji;
                        updatedProperty.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.model().onUpdated(updatedProperty);
                        this.layoutStore.closePopup(this.model().id);
                    });
            } else if (setup.type === PmsType.StayNTouch) {
                const { baseUrl, tokenUrl, hotelId, clientId, clientSecret } = setup as StayNTouchPmsSetup;
                this.propertiesService
                    .configureSNT(
                        organization.id,
                        property.code,
                        setup.type,
                        hotelId,
                        clientId,
                        clientSecret,
                        baseUrl,
                        tokenUrl,
                    )
                    .subscribe(() => {
                        const updatedProperty = property;
                        updatedProperty.pms = PmsType.StayNTouch;
                        updatedProperty.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.model().onUpdated(updatedProperty);
                        this.layoutStore.closePopup(this.model().id);
                    });
            } else {
                this.propertiesService
                    .configurePms(organization, property, setup, user)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => {
                            const updatedProperty = property;
                            updatedProperty.status = {
                                msg: PmsStatusMsg.Connecting,
                                lastUpdate: new Date().toISOString(),
                            };
                            this.model().onUpdated(updatedProperty);
                            this.layoutStore.closePopup(this.model().id);
                        },
                        error: () => {
                            this.layoutStore.addTimedToast(
                                new Toast(uuidv4(), ['Something went wrong, please try again.']),
                            );
                            this.isSaving.set(false);
                        },
                    });
            }
        }
    }

    protected onBack(): void {
        this.currentStep.set(ManagePmsStep.SelectPms);
    }

    protected onCancel(): void {
        this.layoutStore.closePopup(this.model().id);
    }
}
